import React, { useState, useEffect } from 'react'
import './SmartRecommendation.css'

interface Recommendation {
  id: number
  type: 'activity' | 'goal' | 'topic'
  title: string
  description: string
  reason: string
  tags: string[]
  difficulty: 'easy' | 'medium' | 'hard'
  estimatedTime: string
  category: string
  icon: string
}

interface UserData {
  emotions: string[]
  activities: string[]
  goals: string[]
  preferences: string[]
}

const SmartRecommendation: React.FC = () => {
  const [recommendations, setRecommendations] = useState<Recommendation[]>([])
  const [selectedCategory, setSelectedCategory] = useState('all')
  const [userData] = useState<UserData>({
    emotions: ['开心', '浪漫', '温馨'],
    activities: ['做饭', '看电影', '散步', '旅行'],
    goals: ['学习新技能', '健身计划', '一起旅行'],
    preferences: ['户外活动', '文化体验', '美食探索']
  })

  const categories = [
    { value: 'all', label: '全部推荐', icon: '🌟' },
    { value: 'activity', label: '活动推荐', icon: '🎯' },
    { value: 'goal', label: '目标推荐', icon: '🏆' },
    { value: 'topic', label: '话题推荐', icon: '💬' }
  ]

  // 模拟智能推荐算法
  const generateRecommendations = () => {
    const baseRecommendations: Recommendation[] = [
      {
        id: 1,
        type: 'activity',
        title: '一起做手工甜点',
        description: '根据你们喜欢做饭的记录，推荐尝试制作马卡龙或提拉米苏，增加生活情趣。',
        reason: '基于你们的"做饭"活动记录和"开心"情感标签',
        tags: ['做饭', '甜点', '创意'],
        difficulty: 'medium',
        estimatedTime: '2-3小时',
        category: '美食体验',
        icon: '🧁'
      },
      {
        id: 2,
        type: 'activity',
        title: '夜晚观星约会',
        description: '找一个光污染较少的地方，带上毯子和热饮，一起观察星座，分享彼此的梦想。',
        reason: '基于你们的"浪漫"情感记录和"户外活动"偏好',
        tags: ['浪漫', '户外', '夜晚'],
        difficulty: 'easy',
        estimatedTime: '2-4小时',
        category: '浪漫约会',
        icon: '⭐'
      },
      {
        id: 3,
        type: 'goal',
        title: '学习摄影技巧',
        description: '一起学习摄影，为旅行和日常生活记录更美好的瞬间。可以从手机摄影开始。',
        reason: '基于你们的旅行目标和回忆记录习惯',
        tags: ['学习', '摄影', '技能'],
        difficulty: 'medium',
        estimatedTime: '1-2个月',
        category: '技能提升',
        icon: '📸'
      },
      {
        id: 4,
        type: 'topic',
        title: '分享童年趣事',
        description: '互相分享小时候最有趣或最难忘的经历，了解彼此的成长故事。',
        reason: '基于你们的"温馨"情感记录，适合增进了解',
        tags: ['聊天', '回忆', '了解'],
        difficulty: 'easy',
        estimatedTime: '30-60分钟',
        category: '深度交流',
        icon: '👶'
      },
      {
        id: 5,
        type: 'activity',
        title: '制作情侣相册',
        description: '整理你们的照片，制作一本实体相册或数字相册，记录美好时光。',
        reason: '基于你们丰富的回忆记录和照片收集习惯',
        tags: ['回忆', '创意', '纪念'],
        difficulty: 'medium',
        estimatedTime: '3-5小时',
        category: '纪念制作',
        icon: '📖'
      },
      {
        id: 6,
        type: 'goal',
        title: '挑战新运动项目',
        description: '尝试攀岩、滑雪或瑜伽等新运动，既能锻炼身体又能增进默契。',
        reason: '基于你们的健身计划和挑战精神',
        tags: ['运动', '挑战', '健康'],
        difficulty: 'hard',
        estimatedTime: '持续进行',
        category: '运动健身',
        icon: '🏃'
      },
      {
        id: 7,
        type: 'topic',
        title: '规划梦想之家',
        description: '一起讨论理想中的家是什么样子，从装修风格到家具摆设，畅想未来生活。',
        reason: '基于你们的未来规划兴趣和共同目标',
        tags: ['未来', '规划', '家庭'],
        difficulty: 'easy',
        estimatedTime: '1-2小时',
        category: '未来规划',
        icon: '🏠'
      },
      {
        id: 8,
        type: 'activity',
        title: '探索本地文化',
        description: '参观当地博物馆、艺术展或历史景点，了解所在城市的文化底蕴。',
        reason: '基于你们的"文化体验"偏好和探索精神',
        tags: ['文化', '探索', '学习'],
        difficulty: 'easy',
        estimatedTime: '半天',
        category: '文化体验',
        icon: '🏛️'
      }
    ]

    // 根据用户数据调整推荐权重
    const weightedRecommendations = baseRecommendations.map(rec => {
      let score = 0
      
      // 基于情感标签匹配
      rec.tags.forEach(tag => {
        if (userData.emotions.includes(tag)) score += 3
        if (userData.activities.includes(tag)) score += 2
        if (userData.preferences.includes(tag)) score += 1
      })
      
      return { ...rec, score }
    })

    // 按分数排序并返回
    return weightedRecommendations.sort((a, b) => b.score - a.score)
  }

  useEffect(() => {
    const newRecommendations = generateRecommendations()
    setRecommendations(newRecommendations)
  }, [userData])

  const filteredRecommendations = selectedCategory === 'all' 
    ? recommendations 
    : recommendations.filter(rec => rec.type === selectedCategory)

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'easy': return '#4CAF50'
      case 'medium': return '#FF9800'
      case 'hard': return '#F44336'
      default: return '#666'
    }
  }

  const handleTryRecommendation = (recommendation: Recommendation) => {
    // 这里可以添加将推荐转换为实际目标或活动的逻辑
    alert(`准备尝试：${recommendation.title}！`)
  }

  const handleRemindLater = (recommendation: Recommendation) => {
    // 这里可以添加稍后提醒的逻辑
    alert(`已设置稍后提醒：${recommendation.title}`)
  }

  return (
    <div className="smart-recommendation">
      <div className="recommendation-header">
        <h2 className="recommendation-title">智能推荐 🤖</h2>
        <p className="recommendation-subtitle">
          基于你们的情感记录和活动偏好，为你们推荐合适的活动、目标和话题
        </p>
      </div>

      <div className="recommendation-stats">
        <div className="stat-card">
          <div className="stat-icon">📊</div>
          <div className="stat-content">
            <div className="stat-number">{recommendations.length}</div>
            <div className="stat-label">个性化推荐</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">🎯</div>
          <div className="stat-content">
            <div className="stat-number">{userData.emotions.length}</div>
            <div className="stat-label">情感标签</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">⚡</div>
          <div className="stat-content">
            <div className="stat-number">{userData.activities.length}</div>
            <div className="stat-label">活动记录</div>
          </div>
        </div>
      </div>

      <div className="recommendation-filters">
        {categories.map(category => (
          <button
            key={category.value}
            className={`filter-btn ${selectedCategory === category.value ? 'active' : ''}`}
            onClick={() => setSelectedCategory(category.value)}
          >
            <span className="filter-icon">{category.icon}</span>
            <span className="filter-label">{category.label}</span>
          </button>
        ))}
      </div>

      <div className="recommendations-grid">
        {filteredRecommendations.map(recommendation => (
          <div key={recommendation.id} className="recommendation-card">
            <div className="card-header">
              <div className="recommendation-icon">{recommendation.icon}</div>
              <div className="recommendation-type">
                {categories.find(c => c.value === recommendation.type)?.label}
              </div>
            </div>
            
            <h3 className="recommendation-title">{recommendation.title}</h3>
            <p className="recommendation-description">{recommendation.description}</p>
            
            <div className="recommendation-reason">
              <span className="reason-label">推荐理由：</span>
              <span className="reason-text">{recommendation.reason}</span>
            </div>
            
            <div className="recommendation-details">
              <div className="detail-item">
                <span className="detail-label">难度：</span>
                <span 
                  className="difficulty-badge"
                  style={{ backgroundColor: getDifficultyColor(recommendation.difficulty) }}
                >
                  {recommendation.difficulty === 'easy' ? '简单' : 
                   recommendation.difficulty === 'medium' ? '中等' : '困难'}
                </span>
              </div>
              <div className="detail-item">
                <span className="detail-label">预计时间：</span>
                <span className="detail-value">{recommendation.estimatedTime}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">分类：</span>
                <span className="detail-value">{recommendation.category}</span>
              </div>
            </div>
            
            <div className="recommendation-tags">
              {recommendation.tags.map(tag => (
                <span key={tag} className="tag">#{tag}</span>
              ))}
            </div>
            
            <div className="recommendation-actions">
              <button 
                className="btn btn-primary"
                onClick={() => handleTryRecommendation(recommendation)}
              >
                立即尝试
              </button>
              <button 
                className="btn btn-secondary"
                onClick={() => handleRemindLater(recommendation)}
              >
                稍后提醒
              </button>
            </div>
          </div>
        ))}
      </div>

      <div className="recommendation-tips">
        <h3 className="tips-title">💡 推荐小贴士</h3>
        <div className="tips-list">
          <div className="tip-item">
            <span className="tip-icon">🎯</span>
            <span className="tip-text">推荐会根据你们的情感记录和活动偏好实时更新</span>
          </div>
          <div className="tip-item">
            <span className="tip-icon">⭐</span>
            <span className="tip-text">尝试推荐的活动后，记得记录你们的感受哦</span>
          </div>
          <div className="tip-item">
            <span className="tip-icon">🔄</span>
            <span className="tip-text">多样化的活动记录能获得更精准的推荐</span>
          </div>
        </div>
      </div>
    </div>
  )
}

export default SmartRecommendation